/**
 * CRM shared UI helpers: the canonical lead pipeline (lifecycle statuses with
 * labels + badge styling), relative/day time formatting for the inbox and
 * timeline, E.164 phone display, and a session-cached team (assignable
 * recruiters) hook shared by the inbox owner column and the detail page.
 */
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CrmForm, LastComm, MilestoneKey, StudyDetailResponse, TeamMember } from './types';

// ---------------------------------------------------------------------------
// Lead pipeline (lifecycle_status)
// ---------------------------------------------------------------------------

export interface LifecycleMeta {
  value: string;
  label: string;
  /** Badge classes (bg + text, light + dark). */
  badge: string;
  /** Small status-dot color. */
  dot: string;
}

/**
 * Canonical recruiter pipeline, in funnel order — the CONTACT vocabulary
 * (crm.contacts.lifecycle_status), mirroring backend LIFECYCLE_VALUES exactly.
 * Unknown values are tolerated. Contact-only by design: see
 * MEMBERSHIP_ONLY_LIFECYCLES below for why the per-study terminals live apart.
 */
export const LIFECYCLES: LifecycleMeta[] = [
  { value: 'new',                       label: 'New',                         badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',         dot: 'bg-cyan-500' },
  { value: 'contacting',                label: 'Contacting',                  badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',         dot: 'bg-blue-500' },
  { value: 'qualified_pending_records', label: 'Qualified - Pending Records', badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300', dot: 'bg-indigo-500' },
  { value: 'ready_to_schedule',         label: 'Ready to Schedule',           badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',         dot: 'bg-teal-500' },
  { value: 'scheduled',                 label: 'Scheduled',                   badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',     dot: 'bg-amber-500' },
  { value: 'screened',                  label: 'Screened',                    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300', dot: 'bg-purple-500' },
  { value: 'randomized',                label: 'Randomized',                  badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',     dot: 'bg-green-500' },
  { value: 'screen_failed',             label: 'Screen Failed',               badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',         dot: 'bg-rose-500' },
  { value: 'not_eligible',              label: 'Not Eligible',                badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',             dot: 'bg-red-500' },
  { value: 'not_interested',            label: 'Not Interested',              badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',            dot: 'bg-gray-400' },
  { value: 'unreachable',               label: 'Unreachable',                 badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300', dot: 'bg-orange-500' },
];

/**
 * Membership-only terminals (multi-study lead lists). These exist ONLY on a
 * per-study list row, never on contacts.lifecycle_status — they are the
 * post-randomization outcomes CRIO reports, which have no safe contact-level
 * stage (mapping one would break the CTMS round-trip). Terminal treatment,
 * matching the emerald/rose idiom the other end-of-journey statuses use.
 *
 * Deliberately NOT folded into LIFECYCLES: every consumer that ENUMERATES that
 * list builds contact-level UI — KanbanBoard's columns (each one fetching
 * `?lifecycle=<value>`), the Contacts status tabs and bulk "Set status…" select,
 * CriterionEditor's searchable stages — so including them minted columns, tabs
 * and options no contact can ever occupy, and offered a contact-level write the
 * backend rejects. Surfaces whose subject is a MEMBERSHIP row use
 * MEMBERSHIP_LIFECYCLES below instead.
 */
const MEMBERSHIP_ONLY_LIFECYCLES: LifecycleMeta[] = [
  { value: 'completed',                 label: 'Completed',                   badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  { value: 'discontinued',              label: 'Discontinued',                badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',         dot: 'bg-rose-500' },
];

/**
 * Statuses that exist only on a study-list membership, never on a contact.
 * Mirrors backend MEMBERSHIP_ONLY_STATUSES (services/crmStudyMembership.js).
 */
export const MEMBERSHIP_ONLY_STATUSES = MEMBERSHIP_ONLY_LIFECYCLES.map((l) => l.value);

/**
 * The full per-study status vocabulary WITH badge/label meta, in funnel order:
 * the contact pipeline then the two membership-only terminals. Use this wherever
 * the subject is a study-list membership rather than a contact — the Studies
 * card's status select, the By-Study per-status breakdown chips — so a row
 * sitting at completed/discontinued still renders (and can still be picked).
 */
export const MEMBERSHIP_LIFECYCLES: LifecycleMeta[] = [...LIFECYCLES, ...MEMBERSHIP_ONLY_LIFECYCLES];

/**
 * The same vocabulary as bare values: what a per-study status select may offer,
 * and what PATCH /contacts/:id/studies/:studyId accepts. Mirrors backend
 * MEMBERSHIP_STATUSES.
 */
export const MEMBERSHIP_STATUSES = MEMBERSHIP_LIFECYCLES.map((l) => l.value);


const FALLBACK_META: Omit<LifecycleMeta, 'value' | 'label'> = {
  badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  dot: 'bg-gray-400',
};

/**
 * Statuses where a lead is still "being considered" for a study (the By-Study
 * view's default scope) — the active funnel minus the positive outcome
 * (randomized) and the terminal exits (screen_failed, not_eligible,
 * not_interested, unreachable).
 */
export const CONSIDERING_LIFECYCLES = ['new', 'contacting', 'qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened'];

/**
 * "Actively in play for this study" — entering one of these on a SECOND study
 * while another holds `randomized` is what trips the randomized guard. Mirrors
 * backend ACTIVE_MEMBERSHIP_STATUSES. (Declared after CONSIDERING_LIFECYCLES:
 * it spreads it, so the order is load-bearing.)
 */
export const ACTIVE_MEMBERSHIP_STATUSES = [...CONSIDERING_LIFECYCLES, 'randomized'];

/**
 * Terminal-negative dispositions: the lead has exited the funnel. Excludes
 * `randomized` — a randomized lead IS enrolled in a study.
 *
 * NOTE (owner 2026-08-07): these no longer hide the considering-study selector
 * on the lead page. It used to be replaced by a dead "No study assigned" pill,
 * which meant moving a disqualified lead onto the NEXT study's list — the most
 * common thing you do with one — first required walking the status dropdown
 * back out of its disposition. The status badge already says the lead is
 * closed. Used for the Kanban/bulk reason modal and stage gating only.
 */
export const FINAL_DISPOSITIONS = ['screen_failed', 'not_eligible', 'not_interested', 'unreachable'];

/**
 * Stages that mean "we're scheduling this lead". Moving a lead INTO one of these
 * triggers the scheduling-gate warning when the study's required prescreening
 * milestones aren't all met. Mirrors backend SCHEDULE_GATE_STAGES.
 */
export const SCHEDULE_GATE_STAGES = ['ready_to_schedule', 'scheduled'];

/**
 * Stages whose by-study attribution is a high-stakes reporting fact (screened /
 * randomized / screen_failed drive the enrollment + screen-fail numbers the
 * business plans spend on). A manual move into one of these must confirm WHICH
 * study it applies to — the PATCH carries primary_study_id (single lead) or
 * study_confirmed:true (bulk: each lead keeps its own considering study), and
 * the backend 400s (reason 'study_confirmation_required') without one. Mirrors
 * backend STUDY_ATTRIBUTED_STAGES.
 */
export const STUDY_ATTRIBUTED_STAGES = ['screened', 'randomized', 'screen_failed'];

/**
 * Incoming (landing-form) eligibility verdict — the value captured when the
 * lead first came in (crm.contact_leads.eligibility_status), denormalized onto
 * crm.contacts.incoming_eligibility for filtering + sorting across the inbox,
 * database, and conversation views. Same vocabulary the row study chips render
 * via eligibilityBadge() (components/shared.tsx); listed in sort-rank order
 * (the backend sort=eligibility surfaces them top-to-bottom).
 */
export const INCOMING_ELIGIBILITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'eligible', label: 'Eligible' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'pending', label: 'Incomplete screener' },
  { value: 'not_eligible', label: 'Not eligible' },
];

/**
 * Derived pertinent-milestone labels (single source for the MilestoneChip text
 * and the inbox/conversation Milestone filter options). Keyed by the server's
 * milestone key; listed in priority-cascade order for the filter dropdown.
 */
export const MILESTONE_LABELS: Record<MilestoneKey, string> = {
  needs_reply: 'Needs response',
  call_scheduled: 'Call scheduled',
  call_overdue: 'Call overdue',
  appt_scheduled: 'Appt scheduled',
  needs_first_contact: 'Needs first contact',
  follow_up_due: 'Follow-up due',
  awaiting_records: 'Awaiting records',
  ready_to_schedule: 'Ready to schedule',
  going_stale: 'Going stale',
};

/** Milestone filter options, in priority order (matches the server's cascade). */
export const MILESTONE_FILTER_OPTIONS: MilestoneKey[] = [
  'needs_reply', 'call_scheduled', 'call_overdue', 'appt_scheduled',
  'needs_first_contact', 'follow_up_due', 'awaiting_records', 'ready_to_schedule', 'going_stale',
];

/**
 * Inbound sms/email means the lead is waiting on us; an inbound call only
 * counts when it was missed — an answered call was already handled.
 */
export const MISSED_CALL_STATUSES = new Set(['no-answer', 'busy', 'failed', 'canceled', 'missed']);
export const needsReply = (lc: LastComm | null | undefined): boolean =>
  !!lc &&
  lc.direction === 'inbound' &&
  (lc.channel === 'sms' ||
    lc.channel === 'email' ||
    (lc.channel === 'call' && MISSED_CALL_STATUSES.has((lc.status || '').toLowerCase())));

// ---------------------------------------------------------------------------
// Cross-surface stats invalidation: pages that change conversation/pipeline
// state (archive, bulk updates) dispatch this so the Sidebar badges refetch
// immediately instead of waiting out their 60s poll.
// ---------------------------------------------------------------------------

export const STATS_CHANGED_EVENT = 'crm:stats-changed';

export function notifyStatsChanged(): void {
  try {
    window.dispatchEvent(new Event(STATS_CHANGED_EVENT));
  } catch {
    /* non-browser env */
  }
}

// ---------------------------------------------------------------------------
// File download
// ---------------------------------------------------------------------------

/** Save a Blob to the user's disk via a throwaway anchor. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a CRM document's bytes same-origin (the PHI bucket has no browser CORS,
 * so a presigned S3 URL can't be downloaded directly) and save them to disk.
 * Throws on failure — callers should surface the error.
 */
export async function downloadCrmDocument(docId: string, filename: string): Promise<void> {
  const res = await api.get(`/api/crm/documents/${docId}/file`, { responseType: 'blob' });
  triggerBlobDownload(res.data, filename || 'fax.pdf');
}

// ---------------------------------------------------------------------------
// Persisted UI preferences (sidebar collapse, inbox view mode, ...)
// ---------------------------------------------------------------------------

export function getPref(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(`crm.${key}`) ?? fallback;
  } catch {
    return fallback;
  }
}

export function setPref(key: string, value: string): void {
  try {
    window.localStorage.setItem(`crm.${key}`, value);
  } catch {
    /* private mode / quota — preference just won't persist */
  }
}

/**
 * useState whose value is mirrored to localStorage (namespaced `crm.<key>`), so
 * a filter / sort / view selection survives navigating away from the page and
 * back (the route unmounts and a plain useState would reset to its default).
 *
 * The stored value is JSON. When `initial` is a plain object the persisted
 * value is merged OVER it, so adding a new field to a filters shape later picks
 * up its default instead of arriving as `undefined` from an older saved blob.
 * Drop-in for useState; the setter accepts a value or an updater fn.
 */
export function usePersistedState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    const raw = getPref(key, '');
    if (!raw) return initial;
    try {
      const parsed = JSON.parse(raw) as T;
      if (
        initial && typeof initial === 'object' && !Array.isArray(initial) &&
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ) {
        return { ...initial, ...parsed };
      }
      return parsed;
    } catch {
      return initial; // corrupt/legacy blob — fall back to the default
    }
  });
  useEffect(() => {
    setPref(key, JSON.stringify(state));
  }, [key, state]);
  return [state, setState];
}

/**
 * useIsPhone — true below Tailwind's `sm` breakpoint (640px), i.e. a phone
 * (including the home-screen PWA). For CONDITIONAL RENDERING of stateful
 * controls that must exist exactly once (selects, fetch-on-mount buttons),
 * where the CSS hidden/sm: pair would mount duplicates. Purely visual
 * phone/desktop differences should keep using responsive classes.
 */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState<boolean>(() => {
    try {
      return window.matchMedia('(max-width: 639.98px)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(max-width: 639.98px)');
    } catch {
      return;
    }
    const apply = () => setIsPhone(mq.matches);
    apply(); // re-sync in case the viewport changed between init and effect
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, []);
  return isPhone;
}

/**
 * Meta for a lifecycle value; tolerates unknown/legacy values. Resolves over the
 * FULL membership vocabulary, not just the contact pipeline — badges and labels
 * render per-study rows too (a membership at completed/discontinued must not
 * fall through to the gray unknown-value styling).
 */
export function lifecycleMeta(value: string | null | undefined): LifecycleMeta {
  const v = (value || 'new').toLowerCase();
  const found = MEMBERSHIP_LIFECYCLES.find((l) => l.value === v);
  if (found) return found;
  const label = v.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  return { value: v, label, ...FALLBACK_META };
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * CRM admin = platform super_admin or administrator. Gates study setup, AI
 * artifact publishing, and vocabulary curation in the UI (the backend enforces
 * the same check via requireCrmAdmin).
 */
export function useIsCRMAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'super_admin' || user?.role === 'administrator';
}

/**
 * Whether the current user may edit forms — create/edit/publish/duplicate/
 * delete/import. Item #04 (owner, locked): FULL PARITY — any CRM-entitled user
 * (not just admins) can mutate forms. The Forms UI lives behind CRMApp's
 * ProtectedRoute (the `crm` entitlement + site membership), and the backend
 * mirrors this by gating the forms-mutation routes on `requireCrmAccess` alone
 * (no `requireCrmAdmin`) — so any authenticated user who reached this page is
 * already entitled and may edit. Kept distinct from useIsCRMAdmin so the two
 * gates can diverge later (e.g. finer control via tool_user_roles) without
 * re-conflating "admin" with "can edit forms".
 */
export function canEditForms(user: { id?: string } | null | undefined): boolean {
  return Boolean(user);
}

export function useCanEditForms(): boolean {
  return canEditForms(useAuth().user);
}

// Item #05: forms split into two separately-built groups (`surface`). 'landing'
// is the public WCR ad-page form; 'questionnaire' is the texted /f/<token> form.
export type FormSurface = 'landing' | 'questionnaire';
export const FORM_SURFACES: { value: FormSurface; label: string; plural: string }[] = [
  { value: 'landing', label: 'Landing page form', plural: 'Landing Page Forms' },
  { value: 'questionnaire', label: 'Study questionnaire', plural: 'Study Questionnaires' },
];

/** Resolve a form's surface, tolerating legacy/absent values (defaults to the
 *  texted questionnaire — the column default before/after the item #05 migration). */
export function formSurface(surface: string | null | undefined): FormSurface {
  return surface === 'landing' ? 'landing' : 'questionnaire';
}

/** Human label for a form's group (singular). */
export function formSurfaceLabel(surface: string | null | undefined): string {
  return formSurface(surface) === 'landing' ? 'Landing page form' : 'Study questionnaire';
}

/**
 * Which form SendFormCard's picker opens on for one patient. The default has to
 * be the questionnaire for the study they're being CONSIDERED for — the forms
 * list comes back updated_at-desc, so "first row" was landing recruiters on an
 * unrelated study's questionnaire.
 *
 * Precedence mirrors the respond-options resolver
 * (backend crmRespondOptions.getComprehensiveForm), so the card defaults to the
 * same form the texted 1/2/3 menu would send:
 *   1) the considering study's own published questionnaire (never its landing
 *      form — that one carries a contact-capture card for strangers)
 *   2) the network-wide comprehensive form (the fallback for a study with no
 *      questionnaire of its own)
 *   3) the first published form (legacy behaviour — nothing better to offer)
 *
 * `forms` must already be filtered to published (version > 0) active forms.
 */
export function defaultFormId(
  forms: Pick<CrmForm, 'id' | 'study_id' | 'kind' | 'surface'>[],
  studyId: string | null | undefined,
): string {
  if (studyId) {
    const own = forms.find((f) => f.study_id === studyId && formSurface(f.surface) === 'questionnaire');
    if (own) return own.id;
  }
  return (forms.find((f) => f.kind === 'comprehensive') || forms[0])?.id || '';
}

// ---------------------------------------------------------------------------
// Studies (Study Hub)
// ---------------------------------------------------------------------------

export const STUDY_STATUSES = [
  { value: 'draft',      label: 'Draft',      badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { value: 'recruiting', label: 'Recruiting', badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  { value: 'paused',     label: 'Paused',     badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'closed',     label: 'Closed',     badge: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
] as const;

/** Badge meta for a study status; tolerates unknown values. */
export function studyStatusMeta(value: string | null | undefined) {
  const found = STUDY_STATUSES.find((s) => s.value === value);
  return found || { value: value || 'unknown', label: prettySlug(value || 'unknown'), badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
}

/**
 * Generation gate: a study can Generate-with-AI when a legacy CRM document has
 * extracted text OR, for registry-linked studies, the eReg readiness probe
 * reports a current protocol/ICF/amendment with complete text. Mirrors the
 * backend POST /studies/:id/generate gate — keep the two in sync.
 */
export function studyHasExtractedDoc(detail: StudyDetailResponse): boolean {
  return detail.documents.some((d) => d.extraction_status === 'complete')
    || !!detail.registry?.readiness?.ready;
}

/** True while any document text extraction is still in flight (legacy or eReg). */
export function studyDocsExtracting(detail: StudyDetailResponse): boolean {
  return detail.documents.some((d) => d.extraction_status === 'pending' || d.extraction_status === 'processing')
    || !!detail.registry?.readiness?.pending;
}

// ---------------------------------------------------------------------------
// Prescreens, attempts, disqualifications (Phase B)
// ---------------------------------------------------------------------------

/** Mirrors the crm.disqualifications CHECK + backend whitelist. */
export const DQ_CATEGORIES = [
  { value: 'age', label: 'Age' },
  { value: 'demographics', label: 'Demographics' },
  { value: 'diagnosis', label: 'Diagnosis' },
  { value: 'medication', label: 'Medication' },
  { value: 'labs', label: 'Labs' },
  { value: 'vitals', label: 'Vitals' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'logistics', label: 'Logistics (travel, schedule…)' },
  { value: 'withdrew_interest', label: 'Withdrew interest' },
  { value: 'consent', label: 'Consent' },
  { value: 'washout', label: 'Washout period' },
  { value: 'other', label: 'Other' },
] as const;

// ---------------------------------------------------------------------------
// Disposition reasons (recruiter outcome tracking)
// ---------------------------------------------------------------------------

/**
 * Controlled reason vocabulary per terminal-negative disposition — the labels
 * shown in the LifecycleSelect reason modal and the Dispositions report. Mirrors
 * the backend crmDispositions.DISPOSITION_REASONS codes. not_eligible reuses
 * DQ_CATEGORIES (the prescreen DQ vocabulary) so the two not_eligible paths share
 * one list. The implicit system codes (declined_via_text, no_response) are
 * stamped by automatic paths — they carry labels for the report but are NOT
 * offered to recruiters (see `dispositionReasonOptions`).
 */
export const DISPOSITION_REASONS: Record<string, { value: string; label: string; system?: boolean }[]> = {
  not_interested: [
    { value: 'changed_mind', label: 'Changed their mind' },
    { value: 'time_commitment', label: 'Time commitment too much' },
    { value: 'travel_distance', label: 'Travel / distance' },
    { value: 'compensation', label: 'Compensation too low' },
    { value: 'found_other_study', label: 'Found another study' },
    { value: 'health_concern', label: 'Health / safety concern' },
    { value: 'schedule_conflict', label: 'Work / family conflict' },
    { value: 'no_reason_given', label: 'No reason given' },
    { value: 'declined_via_text', label: 'Declined via text', system: true },
    { value: 'other', label: 'Other' },
  ],
  unreachable: [
    { value: 'no_answer_voicemail', label: 'No answer — left voicemail' },
    { value: 'no_answer_no_voicemail', label: 'No answer — no voicemail' },
    { value: 'wrong_number', label: 'Wrong / disconnected number' },
    { value: 'no_reply_texts', label: 'Never replied to texts' },
    { value: 'asked_to_stop', label: 'Asked us to stop' },
    { value: 'no_response', label: 'Exhausted attempts (auto)', system: true },
    { value: 'other', label: 'Other' },
  ],
  screen_failed: [
    { value: 'failed_inclusion', label: 'Failed inclusion criterion' },
    { value: 'met_exclusion', label: 'Met exclusion criterion' },
    { value: 'failed_labs', label: 'Failed labs' },
    { value: 'failed_vitals', label: 'Failed vitals' },
    { value: 'withdrew_during_screening', label: 'Withdrew during screening' },
    { value: 'no_show_screening', label: 'No-show for screening' },
    { value: 'pi_decision', label: 'PI decision' },
    { value: 'other', label: 'Other' },
  ],
};

/** The full reason list for a disposition status (DQ categories for not_eligible). */
export function dispositionReasonsFor(status: string): { value: string; label: string; system?: boolean }[] {
  if (status === 'not_eligible') return DQ_CATEGORIES.map((c) => ({ value: c.value, label: c.label }));
  return DISPOSITION_REASONS[status] || [];
}

/** The reasons a RECRUITER may pick in the modal (excludes system-only codes). */
export function dispositionReasonOptions(status: string): { value: string; label: string }[] {
  return dispositionReasonsFor(status).filter((r) => !r.system);
}

/** Human label for a (status, reason_code) pair; tolerates unknown codes. */
export function dispositionReasonLabel(status: string, code: string | null | undefined): string {
  if (!code) return 'No reason recorded';
  const found = dispositionReasonsFor(status).find((r) => r.value === code);
  return found?.label || code.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * The named outcomes the recruiter Dispositions matrix breaks down — the positive
 * milestones the owner tracks plus the four negatives. Mirrors the SQL ANY(...)
 * list in crm.disposition_stats and the backend DISPOSITION_REPORT_STATUSES.
 */
export const DISPOSITION_REPORT_STATUSES = [
  'qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened',
  'randomized', 'screen_failed', 'not_eligible', 'not_interested', 'unreachable',
];

/** Badge classes per attempt status; tolerates unknown values. */
export function attemptStatusMeta(status: string | null | undefined) {
  const map: Record<string, { label: string; badge: string }> = {
    // "Prescreening" (not "Screening") — a prescreen questionnaire is PRE-consent.
    // "Screening" is reserved for post-ICF official screening into a study.
    screening:    { label: 'Prescreening',  badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
    eligible:     { label: 'Passed',        badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
    disqualified: { label: 'Did not qualify', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
    withdrew:     { label: 'Withdrew',      badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    scheduled:    { label: 'Scheduled',     badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
    enrolled:     { label: 'Enrolled',      badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
    completed:    { label: 'Completed',     badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
    discontinued: { label: 'Discontinued',  badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  };
  return map[status || ''] || { label: prettySlug(status || 'unknown'), badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
}

export type RuleVerdict = 'pass' | 'fail' | 'unknown';

/**
 * Client-side mirror of a criterion's eligibility rule (the rule expresses the
 * PASSING condition on the answer). Empty rule / no answer -> 'unknown'
 * (recruiter judgment) — never auto-fail what we can't evaluate.
 */
/**
 * Loose equality across the type seams the rule pipeline allows: saved choice
 * answers are strings while AI/admin-authored rule values may be numbers (and
 * vice versa). '1' must match 1 — a strict compare would mint false FAILs.
 */
const looseEq = (a: any, b: any): boolean =>
  a === b || (a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b));

export function evalCriterionRule(rule: any, value: any): RuleVerdict {
  if (!rule || typeof rule !== 'object') return 'unknown';
  // Group node: combine child verdicts with strong-Kleene 3-valued logic, so a
  // condition we can't evaluate folds to 'unknown' (recruiter judgment) rather
  // than auto-failing. Checked BEFORE the empty-value guard so an empty answer
  // inside a group still resolves per-leaf. (Keep in lock-step with the backend
  // port in crmPivot.js.)
  if (rule.combinator && Array.isArray(rule.conditions)) {
    if (rule.conditions.length === 0) return 'unknown';
    const verdicts: RuleVerdict[] = rule.conditions.map((c: any) => evalCriterionRule(c, value));
    if (rule.combinator === 'or') {
      if (verdicts.includes('pass')) return 'pass';
      if (verdicts.includes('unknown')) return 'unknown';
      return 'fail';
    }
    if (verdicts.includes('fail')) return 'fail';     // 'and'
    if (verdicts.includes('unknown')) return 'unknown';
    return 'pass';
  }
  if (!rule.op) return 'unknown';
  if (value === null || value === undefined || value === '') return 'unknown';
  switch (rule.op) {
    case 'eq': return looseEq(value, rule.value) ? 'pass' : 'fail';
    case 'neq': return !looseEq(value, rule.value) ? 'pass' : 'fail';
    case 'gt': return typeof value === 'number' && value > rule.value ? 'pass' : typeof value === 'number' ? 'fail' : 'unknown';
    case 'gte': return typeof value === 'number' && value >= rule.value ? 'pass' : typeof value === 'number' ? 'fail' : 'unknown';
    case 'lt': return typeof value === 'number' && value < rule.value ? 'pass' : typeof value === 'number' ? 'fail' : 'unknown';
    case 'lte': return typeof value === 'number' && value <= rule.value ? 'pass' : typeof value === 'number' ? 'fail' : 'unknown';
    case 'between':
      if (typeof value !== 'number') return 'unknown';
      return value >= rule.min && value <= rule.max ? 'pass' : 'fail';
    case 'in': {
      if (!Array.isArray(rule.values)) return 'fail';
      // Multi-select (checklist) answers save as an array — the rule passes
      // when ANY selected value is in the allowed set, not when the whole
      // array matches a single allowed value (which can never happen). A
      // comma-joined STRING ("mi, stroke" — the shape older captures stored
      // before choice answers were canonicalized at capture) contributes its
      // split parts as candidates too; the full string stays a candidate
      // first, so an option value that itself contains a comma still matches
      // exactly. (Keep in lock-step with the backend port in crmPivot.js.)
      const selected: any[] = Array.isArray(value)
        ? value
        : [value, ...(typeof value === 'string' && value.includes(',')
          ? value.split(',').map((s) => s.trim()).filter(Boolean)
          : [])];
      return selected.some((sel: any) => rule.values.some((v: any) => looseEq(v, sel))) ? 'pass' : 'fail';
    }
    case 'exists': return 'pass';
    default: return 'unknown';
  }
}

/**
 * Compute derived (calculation) criteria — age from a DOB answer, BMI from
 * weight (lbs) + height (inches) answers — and return a NEW answers map (keyed
 * by question_key) with the computed values filled in. A missing/unparseable
 * input leaves the derived value absent, so it evaluates to 'unknown' (recruiter
 * judgment) rather than a spurious fail. Client-side mirror of
 * crmForms.resolveCalculations, adapted to the prescreen's inches/lbs inputs.
 */
export function resolveCalculations(
  criteria: { question_key: string; answer_type?: string; calculation?: { preset: string; inputs: Record<string, string> } | null }[],
  answers: Record<string, any>
): Record<string, any> {
  const resolved = { ...answers };
  const present = (v: any) => v !== undefined && v !== null && v !== '';
  // Pre-pass: a `height` answer is feet+inches captured as a total-inches STRING
  // (the runner's HeightInput). Coerce it to a number so direct height rules
  // evaluate numerically and the BMI calc below reads a number. Runs first so a
  // height fed into a BMI slot is already numeric when the calc reads it.
  for (const c of criteria) {
    if (c.answer_type !== 'height' || !present(resolved[c.question_key])) continue;
    const n = Number(resolved[c.question_key]);
    if (Number.isFinite(n)) resolved[c.question_key] = n;
  }
  for (const c of criteria) {
    const calc = c.calculation;
    if (!calc) continue;
    if (calc.preset === 'age') {
      const dob = calc.inputs?.dateOfBirth ? resolved[calc.inputs.dateOfBirth] : undefined;
      if (typeof dob === 'string' && dob) {
        const d = new Date(dob);
        if (!Number.isNaN(d.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - d.getFullYear();
          const m = today.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
          resolved[c.question_key] = age;
        }
      }
    } else if (calc.preset === 'bmi') {
      const weight = calc.inputs?.weightKg ? resolved[calc.inputs.weightKg] : undefined;
      const height = calc.inputs?.heightCm ? resolved[calc.inputs.heightCm] : undefined;
      if (present(weight) && present(height)) {
        const weightKg = Number(weight) * 0.453592; // lbs -> kg
        const heightCm = Number(height) * 2.54;      // inches -> cm
        if (weightKg > 0 && heightCm > 0) {
          const heightM = heightCm / 100;
          resolved[c.question_key] = Math.round((weightKg / (heightM * heightM)) * 10) / 10;
        }
      }
    } else if (calc.preset === 'percent_change' || calc.preset === 'percent_decrease' || calc.preset === 'difference') {
      // Two-point change ("% weight loss in the last 3 months"). Same sign
      // convention as crmForms.resolveCalculations: percent_decrease is
      // POSITIVE when the value went down; a zero baseline has no percentage.
      const baseline = calc.inputs?.baseline ? resolved[calc.inputs.baseline] : undefined;
      const current = calc.inputs?.current ? resolved[calc.inputs.current] : undefined;
      if (present(baseline) && present(current)) {
        const b = Number(baseline);
        const cur = Number(current);
        if (Number.isFinite(b) && Number.isFinite(cur)) {
          if (calc.preset === 'difference') resolved[c.question_key] = Math.round((cur - b) * 10) / 10;
          else if (b !== 0) {
            const pct = ((cur - b) / b) * 100;
            resolved[c.question_key] = Math.round((calc.preset === 'percent_decrease' ? -pct : pct) * 10) / 10;
          }
        }
      }
    }
  }
  return resolved;
}

/** Typed-columns row -> the plain value the runner/rule evaluator works with. */
export function answerValue(a: { value_text: string | null; value_num: number | null; value_bool: boolean | null; value_json: unknown }): any {
  if (a.value_bool !== null && a.value_bool !== undefined) return a.value_bool;
  if (a.value_num !== null && a.value_num !== undefined) return a.value_num;
  if (a.value_text !== null && a.value_text !== undefined) return a.value_text;
  return a.value_json ?? null;
}

/** Human-readable answer value for a criterion (Yes/No, the chosen option's
 *  label, or the raw value) — used to record WHAT the patient answered. */
export function formatCriterionAnswer(
  criterion: { answer_type: string; options?: { value: string; label: string }[] | null },
  value: any
): string {
  if (value === null || value === undefined || value === '') return '(no answer)';
  if (criterion.answer_type === 'boolean') {
    const truthy = value === true || ['true', 'yes', '1'].includes(String(value).toLowerCase());
    return truthy ? 'Yes' : 'No';
  }
  if (criterion.answer_type === 'choice') {
    const opt = (criterion.options || []).find((o) => String(o.value) === String(value));
    return opt?.label || String(value);
  }
  if (criterion.answer_type === 'height') {
    // Stored as total inches — show it the way it was reported (e.g. 5 ft 8 in).
    const total = Number(value);
    if (Number.isFinite(total) && total > 0) return `${Math.floor(total / 12)} ft ${total % 12} in`;
  }
  return String(value);
}

/** The bare clause of a rule node (no "eligible if" prefix), recursing AND/OR
 *  groups and joining children with " and " / " or " (or '' when empty). */
function ruleClause(rule: any, answerType: string): string {
  if (!rule || typeof rule !== 'object') return '';
  if (rule.combinator && Array.isArray(rule.conditions)) {
    const parts = rule.conditions.map((c: any) => ruleClause(c, answerType)).filter(Boolean);
    if (!parts.length) return '';
    const joined = parts.join(rule.combinator === 'or' ? ' or ' : ' and ');
    return parts.length > 1 ? `(${joined})` : joined;
  }
  if (!rule.op) return '';
  const yn = (v: any) => (answerType === 'boolean' ? (v ? 'Yes' : 'No') : String(v));
  switch (rule.op) {
    case 'eq': return yn(rule.value);
    case 'neq': return `not ${yn(rule.value)}`;
    case 'gt': return `> ${rule.value}`;
    case 'gte': return `≥ ${rule.value}`;
    case 'lt': return `< ${rule.value}`;
    case 'lte': return `≤ ${rule.value}`;
    case 'between': return `${rule.min}–${rule.max}`;
    case 'in': return `one of ${(rule.values || []).join(', ')}`;
    case 'exists': return 'answered';
    default: return '';
  }
}

/** Short "eligible if …" phrasing of a criterion's PASSING rule (or '' when none).
 *  Handles both a single leaf and an AND/OR group. */
export function ruleSummaryText(rule: any, answerType: string): string {
  const clause = ruleClause(rule, answerType);
  // A top-level group is already parenthesized by ruleClause; strip the wrap
  // so the summary reads "eligible if ≥ 18 and ≤ 65" rather than with parens.
  const inner = clause.startsWith('(') && clause.endsWith(')') ? clause.slice(1, -1) : clause;
  return inner ? `eligible if ${inner}` : '';
}

/** One line describing a failing answer for the DQ "details" field, e.g.
 *  "Age: 17 (eligible if 18–65)" — records the actual disqualifying value, not
 *  just the question. */
export function describeFailingAnswer(
  criterion: { display_question: string; answer_type: string; options?: { value: string; label: string }[] | null; rule: any },
  value: any
): string {
  const ans = formatCriterionAnswer(criterion, value);
  const rule = ruleSummaryText(criterion.rule, criterion.answer_type);
  return rule ? `${criterion.display_question}: ${ans} (${rule})` : `${criterion.display_question}: ${ans}`;
}

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

/**
 * Compact relative time for list rows: "now", "5m", "3h", "2d", else "Jun 5".
 * Future timestamps (e.g. an automation's next step) read "in 5m" / "in 3h".
 */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  let sec = Math.floor((Date.now() - then) / 1000);
  if (sec < -60) {
    sec = -sec;
    if (sec < 3600) return `in ${Math.floor(sec / 60)}m`;
    if (sec < 86400) return `in ${Math.floor(sec / 3600)}h`;
    if (sec < 14 * 86400) return `in ${Math.floor(sec / 86400)}d`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)}d`;
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === new Date().getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

/**
 * Absolute date + time, e.g. "Jun 22, 2:39 PM" (the year is appended only when
 * it isn't the current year). Use when the exact moment matters more than a
 * relative "how long ago" — e.g. when a lead actually came in.
 */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  };
  return d.toLocaleString(undefined, opts);
}

/** Day separator label for the timeline: "Today", "Yesterday", "Mon, Jun 9". */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === today.getFullYear()
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

/** Local calendar-day key (YYYY-MM-DD) for grouping timeline items. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Clock time for timeline items: "2:34 PM". */
export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Full date + time for a conversation message, e.g. "Mon Jul 6, 2026 3:54pm".
 * The weekday and "Mon Jul 6, 2026" pieces are joined manually so there's no
 * comma after the weekday, and the meridiem is lowercased with no leading space.
 */
export function messageTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(/\s*(AM|PM)$/i, (_m, ap: string) => ap.toLowerCase());
  return `${weekday} ${date} ${time}`;
}

/**
 * Resolve a Reports range token to an explicit [from, to) window (ISO or null).
 *   'today'     → since local midnight, open-ended (to = now)
 *   'yesterday' → the full prior local calendar day [start, today-midnight)
 *   'all'       → unbounded
 *   '<N>'       → the last N days, open-ended (the original rolling window)
 * 'yesterday' is why the reports thread an explicit `to`: every other token
 * leaves it null (= now), matching the prior behavior.
 */
export function rangeWindow(range: string): { from: string | null; to: string | null } {
  if (range === 'all') return { from: null, to: null };
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === 'today') return { from: startOfToday.toISOString(), to: null };
  if (range === 'yesterday') {
    const startYesterday = new Date(startOfToday.getTime() - 86400e3);
    return { from: startYesterday.toISOString(), to: startOfToday.toISOString() };
  }
  const days = Number(range);
  if (Number.isFinite(days) && days > 0) {
    return { from: new Date(now.getTime() - days * 86400e3).toISOString(), to: null };
  }
  return { from: null, to: null };
}

// ---------------------------------------------------------------------------
// Tasks (follow-up reminders)
// ---------------------------------------------------------------------------

/** Task statuses that still need action (the "open" set for filters). */
export const TASK_OPEN_STATUSES = 'open,in_progress';

export const TASK_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
] as const;

/**
 * Badge classes for a task priority chip — only high/urgent get a flag (normal
 * and low render nothing so the common case stays uncluttered).
 */
export function priorityBadge(priority: string | null | undefined): string | null {
  if (priority === 'urgent') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (priority === 'high') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  return null;
}

export type DueBucket = 'overdue' | 'today' | 'upcoming' | 'none';

/** Bucket a due timestamp: past -> overdue, later this local day -> today. */
export function dueBucket(iso: string | null | undefined): DueBucket {
  if (!iso) return 'none';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'none';
  if (t <= Date.now()) return 'overdue';
  return dayKey(iso) === dayKey(new Date().toISOString()) ? 'today' : 'upcoming';
}

/** Due chip copy: "Overdue · 3h", "Due in 2h", "Due Jun 15". */
export function dueLabel(iso: string): string {
  const rel = relTime(iso);
  return dueBucket(iso) === 'overdue' ? `Overdue · ${rel}` : `Due ${rel}`;
}

/** Chip classes per due bucket (none -> muted, for the no-due-date chip). */
export function dueChipCls(bucket: DueBucket): string {
  if (bucket === 'overdue') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (bucket === 'today') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Pretty-print a +1 E.164 number as (XXX) XXX-XXXX; pass through others. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '—';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/**
 * Best-effort E.164 normalization for a dialer's free-typed number (US default).
 * Returns null when the input can't be confidently dialed, so the Call button
 * stays disabled rather than risking a mis-dial:
 *   "+15551234567" -> "+15551234567"  (already E.164, passed through)
 *   "(555) 123-4567" / "5551234567" -> "+15551234567"  (10 digits => US)
 *   "15551234567" -> "+15551234567"  (11 digits leading 1)
 */
export function toE164US(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (/^\+[1-9]\d{6,14}$/.test(trimmed)) return trimmed; // already valid E.164
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * Progressive hyphen-formatter for a US phone <input> value. Reformats from the
 * raw digits on every change so typing AND backspacing both behave — deleting a
 * hyphen just drops the preceding digit on the next keystroke.
 *   ""            -> ""
 *   "5"           -> "5"
 *   "555123"      -> "555-123"
 *   "5551234567"  -> "555-123-4567"
 *   "15551234567" -> "555-123-4567"  (leading US country code dropped)
 */
export function formatPhoneInput(raw: string): string {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Up-to-two-letter initials for an avatar chip. */
export function initials(name: string | null | undefined): string {
  const n = (name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/);
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Title-case a slug-ish value for display ("facebook_ads" -> "Facebook Ads"). */
export function prettySlug(slug: string | null | undefined): string {
  if (!slug) return '—';
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

// ---------------------------------------------------------------------------
// Lead-detail back navigation
// ---------------------------------------------------------------------------
// Where the user was before opening a lead. CRMRoutes records every non-lead
// CRM location (path + query) here, so ContactDetail's back button can return
// to the actual origin — Patient database, By study, a Conversations view,
// Today, a report — instead of a hard-coded Leads inbox. Per-tab
// (sessionStorage) so it survives a reload while viewing the lead, and paths
// under /crm/contacts/ are never recorded so a lead → eligibility-review →
// lead round trip keeps the original origin.

const LEAD_ORIGIN_KEY = 'crm.leadOrigin';

export function rememberLeadOrigin(pathAndSearch: string): void {
  try { sessionStorage.setItem(LEAD_ORIGIN_KEY, pathAndSearch); } catch { /* private mode */ }
}

/** The recorded origin, or null on a fresh tab / deep link straight to a lead. */
export function getLeadOrigin(): string | null {
  try { return sessionStorage.getItem(LEAD_ORIGIN_KEY); } catch { return null; }
}

// Sidebar wording, so the back button names the page it returns to.
const LEAD_ORIGIN_LABELS: Array<[string, string]> = [
  ['/crm/inbox', 'Lead Inbox'],
  ['/crm/database', 'Patient database'],
  ['/crm/active', 'Active conversations'],
  ['/crm/awaiting', 'No reply yet'],
  ['/crm/by-study', 'By study'],
  ['/crm/calendar', 'Calendar'],
  ['/crm/reports', 'Reports'],
  ['/crm/phone', 'Phone'],
  ['/crm/fax', 'Fax'],
];

/** Display label for a back target ("Back" for anything unrecognized). */
export function leadOriginLabel(pathAndSearch: string | null): string {
  const path = (pathAndSearch || '').split('?')[0];
  if (path === '/crm' || path === '/crm/') return 'Today';
  const hit = LEAD_ORIGIN_LABELS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  return hit ? hit[1] : 'Back';
}

// ---------------------------------------------------------------------------
// Team (assignable recruiters) — fetched once per session and shared
// ---------------------------------------------------------------------------
//
// NOTE: the cached rows now carry out_today / out_until (out-of-office). That
// is the one field on a team member that goes stale WITHIN a session — mark
// someone out and every owner picker keeps offering them as available until the
// tab is reloaded. `invalidateTeam()` below is what the absence writes call.

let teamCache: TeamMember[] | null = null;
let teamPromise: Promise<TeamMember[]> | null = null;
// Every mounted useTeam() instance subscribes here, so one successful retry
// (from any OwnerSelect) repopulates the whole screen.
const teamListeners = new Set<() => void>();
const notifyTeam = () => teamListeners.forEach((l) => l());

/** Resolves the cached team; REJECTS on failure (callers surface the error). */
export async function fetchTeam(): Promise<TeamMember[]> {
  if (teamCache) return teamCache;
  if (!teamPromise) {
    teamPromise = api
      .get('/api/crm/team')
      .then((res) => {
        teamCache = (res.data?.team || []) as TeamMember[];
        notifyTeam();
        return teamCache;
      })
      .catch((err) => {
        teamPromise = null; // allow retry on the next call
        throw err;
      });
  }
  return teamPromise;
}

/**
 * Drop the cached team so the next read re-fetches.
 *
 * Call this after any write that changes a member's availability — filing or
 * cancelling an absence — otherwise `out_today` stays frozen at whatever it was
 * when the tab first loaded, and OwnerSelect keeps showing an out-of-office
 * teammate as available for the rest of the session. Mounted useTeam()
 * consumers are notified so they refetch immediately rather than on next mount.
 */
export function invalidateTeam(): void {
  teamCache = null;
  teamPromise = null;
  // Clearing alone is NOT enough: useTeam's listener only copies a non-null
  // cache, so a bare notify would leave every mounted picker showing the stale
  // list until it remounted. Re-fetch instead — fetchTeam repopulates and
  // notifies subscribers itself on success. Fire-and-forget with the rejection
  // swallowed: a failed refresh must not surface as an unhandled rejection, and
  // the next fetchTeam() caller retries anyway (teamPromise is cleared on error).
  fetchTeam().catch(() => { /* stale-but-present beats a crash; callers retry */ });
}

/**
 * Team list + id->member map, loaded once and cached for the session.
 * `error` is true when the load failed (team empty); `retry()` re-fetches and,
 * on success, updates every mounted subscriber.
 */
export function useTeam(): {
  team: TeamMember[];
  byId: Map<string, TeamMember>;
  loading: boolean;
  error: boolean;
  retry: () => void;
} {
  const [team, setTeam] = useState<TeamMember[]>(teamCache || []);
  const [loading, setLoading] = useState(!teamCache);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const sync = () => {
      if (alive && teamCache) {
        setTeam(teamCache);
        setLoading(false);
        setError(false);
      }
    };
    teamListeners.add(sync);
    fetchTeam()
      .then(() => sync())
      .catch(() => {
        if (alive) {
          setLoading(false);
          setError(true);
        }
      });
    return () => {
      alive = false;
      teamListeners.delete(sync);
    };
  }, []);

  const retry = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchTeam()
      .then(() => { /* sync via listener */ })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, []);

  const byId = useMemo(() => new Map(team.map((m) => [m.id, m])), [team]);
  return { team, byId, loading, error, retry };
}
